// ===================================================
// --- ADMIN NEWS SLICE ---
// Path: src/redux/slices/admin/news-slice.ts
// ===================================================

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  NewsState,
  GetNewsParams,
  CreateNewsPayload,
  UpdateNewsPayload,
} from "@/types/admin/news-types";
import { newsService } from "@/services/system-admin/admin-data.service";

const initialState: NewsState = {
  newsList: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
};

// export const fetchNews = createAsyncThunk(
//   "news/fetchNews",
//   async (params: GetNewsParams) => {
//     const response = await newsService.getNews(params);
//     return { ...response, page: params.page, pageSize: params.pageSize };
//   }
// );
export const fetchNews = createAsyncThunk(
  "news/fetchNews",
  async (params: GetNewsParams) => {
    return await newsService.getNews(params);   // response already has page/pageSize
  }
);

export const createNews = createAsyncThunk(
  "news/createNews",
  async (payload: CreateNewsPayload) => {
    return await newsService.createNews(payload);
  }
);

export const updateNews = createAsyncThunk(
  "news/updateNews",
  async ({ uid, payload }: { uid: number; payload: UpdateNewsPayload }) => {
    return await newsService.updateNews(uid, payload);
  }
);

export const deleteNews = createAsyncThunk(
  "news/deleteNews",
  async (uid: number) => {
    await newsService.deleteNews(uid);
    return uid;
  }
);

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        state.loading = false;
        const list =
          action.payload.result?.items ||
          action.payload.items ||
          action.payload.data ||
          [];
        state.newsList = list;
        state.currentPage = action.payload.page || 1;
        state.pageSize = action.payload.pageSize || 20;
        state.totalCount =
          list[0]?.totalRecordCount ||
          action.payload.totalCount ||
          action.payload.result?.totalCount ||
          0;
      })
      .addCase(fetchNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch news";
      })
      // create
      .addCase(createNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNews.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create news";
      })
      // update
      .addCase(updateNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNews.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update news";
      })
      // delete
      .addCase(deleteNews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNews.fulfilled, (state, action) => {
        state.loading = false;
        state.newsList = state.newsList.filter(
          (n) => n.uid !== action.payload
        );
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete news";
      });
  },
});

export const { clearError } = newsSlice.actions;
export default newsSlice.reducer;